/* The Wearable Art theme script (WebForge).
 * Uses Shopify's Ajax cart API (/cart.js, /cart/add.js, /cart/change.js), product JSON (/products/<handle>.js),
 * predictive search and section rendering. Wishlist and recently viewed live in localStorage. */
(() => {
'use strict';

const WA = window.WA || {};
const R = WA.routes || {};
const ROOT = (WA.root || '/').replace(/\/$/, '');

/* ---------- helpers ---------- */
const $ = (sel, root = document) => root.querySelector(sel);
const $$ = (sel, root = document) => [...root.querySelectorAll(sel)];
const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
// Shopify amounts are in paise
const money = (paise) => {
  const r = Number(paise || 0) / 100;
  return '₹' + r.toLocaleString('en-IN', { minimumFractionDigits: r % 1 ? 2 : 0, maximumFractionDigits: 2 });
};
const abs = (src) => (!src ? '' : src.startsWith('//') ? 'https:' + src : src);
const sized = (src, w) => (!src ? '' : abs(src) + (src.includes('?') ? '&' : '?') + 'width=' + w);
const store = {
  get(key, fallback) { try { return JSON.parse(localStorage.getItem(key)) ?? fallback; } catch { return fallback; } },
  set(key, value) { try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* storage unavailable */ } },
};
const debounce = (fn, ms) => { let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), ms); }; };
const page = document.body.dataset.page;
// Festive sale: theme.liquid adds html.is-sale between the sale's start and end
const SALE = WA.sale || null;
const saleOn = () => !!SALE && document.documentElement.classList.contains('is-sale');

const ICON = {
  search: "<svg viewBox=\"0 0 24 24\" fill=\"none\" stroke=\"currentColor\" stroke-width=\"1.6\"><circle cx=\"11\" cy=\"11\" r=\"7\"/><path d=\"M20 20l-3.5-3.5\"/></svg>",
  bag: "<svg viewBox=\"0 0 24 24\" fill=\"none\" stroke=\"currentColor\" stroke-width=\"1.6\"><path d=\"M5 8h14l-1 13H6L5 8z\"/><path d=\"M9 8V6a3 3 0 016 0v2\"/></svg>",
  close: "<svg viewBox=\"0 0 24 24\" fill=\"none\" stroke=\"currentColor\" stroke-width=\"1.6\"><path d=\"M6 6l12 12M18 6L6 18\"/></svg>",
  down: "<svg viewBox=\"0 0 24 24\" fill=\"none\" stroke=\"currentColor\" stroke-width=\"2\"><path d=\"M6 9l6 6 6-6\"/></svg>",
  left: "<svg viewBox=\"0 0 24 24\" fill=\"none\" stroke=\"currentColor\" stroke-width=\"1.8\"><path d=\"M15 6l-6 6 6 6\"/></svg>",
  right: "<svg viewBox=\"0 0 24 24\" fill=\"none\" stroke=\"currentColor\" stroke-width=\"1.8\"><path d=\"M9 6l6 6-6 6\"/></svg>",
  arrow: "<svg viewBox=\"0 0 24 24\" width=\"14\" height=\"14\" fill=\"none\" stroke=\"currentColor\" stroke-width=\"1.8\"><path d=\"M5 12h14M13 6l6 6-6 6\"/></svg>",
  plus: "<svg viewBox=\"0 0 24 24\" fill=\"none\" stroke=\"currentColor\" stroke-width=\"1.8\"><path d=\"M12 5v14M5 12h14\"/></svg>",
  minus: "<svg viewBox=\"0 0 24 24\" fill=\"none\" stroke=\"currentColor\" stroke-width=\"1.8\"><path d=\"M5 12h14\"/></svg>",
  sparkle: "<svg viewBox=\"0 0 24 24\" fill=\"none\" stroke=\"currentColor\" stroke-width=\"1.6\"><path d=\"M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8z\"/><path d=\"M19 15l.7 2 2 .7-2 .7-.7 2-.7-2-2-.7 2-.7z\"/></svg>",
  shield: "<svg viewBox=\"0 0 24 24\" fill=\"none\" stroke=\"currentColor\" stroke-width=\"1.6\"><path d=\"M12 3l7 3v6c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6z\"/><path d=\"M9 12l2 2 4-4\"/></svg>",
  truck: "<svg viewBox=\"0 0 24 24\" fill=\"none\" stroke=\"currentColor\" stroke-width=\"1.6\"><path d=\"M2 6h11v10H2zM13 9h4l4 4v3h-8z\"/><circle cx=\"6\" cy=\"18\" r=\"2\"/><circle cx=\"17\" cy=\"18\" r=\"2\"/></svg>",
  gift: "<svg viewBox=\"0 0 24 24\" fill=\"none\" stroke=\"currentColor\" stroke-width=\"1.6\"><path d=\"M3 9h18v4H3zM5 13v8h14v-8M12 9v12\"/><path d=\"M12 9c-2-4-6-4-6-1.5S10 9 12 9zm0 0c2-4 6-4 6-1.5S14 9 12 9z\"/></svg>",
  diya: "<svg viewBox=\"0 0 24 24\" fill=\"none\" stroke=\"currentColor\" stroke-width=\"1.6\" stroke-linejoin=\"round\"><path class=\"diya-flame\" d=\"M12 2.5c1.7 2.1 2.4 3.6 2.4 4.9a2.4 2.4 0 01-4.8 0c0-1.3.7-2.8 2.4-4.9z\"/><path d=\"M2.5 12.5h19c-.7 4-4.8 6.5-9.5 6.5s-8.8-2.5-9.5-6.5z\"/><path d=\"M8 21.5h8\"/></svg>",
  check: "<svg viewBox=\"0 0 24 24\" fill=\"none\" stroke=\"currentColor\" stroke-width=\"2\"><path d=\"M5 12l4.5 4.5L19 7\"/></svg>",
  instagram: "<svg viewBox=\"0 0 24 24\" fill=\"none\" stroke=\"currentColor\" stroke-width=\"1.6\"><rect x=\"3\" y=\"3\" width=\"18\" height=\"18\" rx=\"5\"/><circle cx=\"12\" cy=\"12\" r=\"4\"/><circle cx=\"17.5\" cy=\"6.5\" r=\"1\" fill=\"currentColor\"/></svg>",
  heart: "<svg viewBox=\"0 0 24 24\" fill=\"none\" stroke=\"currentColor\" stroke-width=\"1.6\"><path d=\"M12 20s-7-4.4-7-10a4 4 0 017-2.6A4 4 0 0119 10c0 5.6-7 10-7 10z\"/></svg>",
  copy: "<svg viewBox=\"0 0 24 24\" fill=\"none\" stroke=\"currentColor\" stroke-width=\"1.6\"><rect x=\"9\" y=\"9\" width=\"11\" height=\"11\" rx=\"2\"/><path d=\"M5 15V5a2 2 0 012-2h10\"/></svg>",
  up: "<svg viewBox=\"0 0 24 24\" fill=\"none\" stroke=\"currentColor\" stroke-width=\"2\"><path d=\"M6 15l6-6 6 6\"/></svg>",
  star4: "<svg viewBox=\"0 0 24 24\" aria-hidden=\"true\"><path d=\"M12 0c.7 6.4 2.9 9.9 12 12-9.1 2.1-11.3 5.6-12 12-.7-6.4-2.9-9.9-12-12 9.1-2.1 11.3-5.6 12-12z\" fill=\"currentColor\"/></svg>"
};
/* ---------- product data ---------- */
const productCache = {};
function getProduct(handle) {
  if (!productCache[handle]) {
    productCache[handle] = fetch(`${ROOT}/products/${encodeURIComponent(handle)}.js`)
      .then((r) => { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then(normalize);
    productCache[handle].catch(() => { delete productCache[handle]; });
  }
  return productCache[handle];
}
// one shape for product.js, the page's product JSON and predictive-search results
function normalize(p) {
  const variants = (p.variants || []).map((v) => ({
    id: v.id,
    title: v.title,
    available: v.available !== false,
    price: typeof v.price === 'string' ? Math.round(parseFloat(v.price) * 100) : v.price,
    compare: v.compare_at_price ? (typeof v.compare_at_price === 'string' ? Math.round(parseFloat(v.compare_at_price) * 100) : v.compare_at_price) : 0,
    opts: v.options || [v.option1, v.option2, v.option3].filter((x) => x != null),
  }));
  let options = (p.options || []).map((o, i) => (typeof o === 'string'
    ? { name: o, values: [...new Set(variants.map((v) => v.opts[i]))] }
    : { name: o.name, values: o.values }));
  if (variants.length === 1 && options.length === 1 && options[0].values.length === 1 && /default title/i.test(options[0].values[0])) options = [];
  const images = (p.images || []).map((im) => (typeof im === 'string' ? im : im.src || im.url)).filter(Boolean);
  if (!images.length && p.featured_image) images.push(typeof p.featured_image === 'string' ? p.featured_image : p.featured_image.url);
  if (!images.length && p.image) images.push(p.image);
  const prices = variants.map((v) => v.price);
  let min = prices.length ? Math.min(...prices) : 0;
  let max = prices.length ? Math.max(...prices) : 0;
  const cheapest = variants.find((v) => v.price === min);
  let compare = cheapest && cheapest.compare > cheapest.price ? cheapest.compare : 0;
  let available = p.available !== false && variants.some((v) => v.available);
  // Predictive search sends no variants (variants: []), so use the product's own price
  // fields there. They come as rupee strings like "449.00".
  if (!variants.length) {
    const paise = (x) => (x == null || x === '' ? 0 : typeof x === 'string' ? Math.round(parseFloat(x) * 100) : x);
    min = paise(p.price_min ?? p.price);
    max = Math.max(min, paise(p.price_max ?? p.price));
    const c = paise(p.compare_at_price_min);
    compare = c > min ? c : 0;
    available = p.available !== false;
  }
  return {
    handle: p.handle,
    title: p.title,
    url: p.url || `${ROOT}/products/${p.handle}`,
    available,
    created: p.created_at || p.published_at || null,
    images,
    variants,
    options,
    min,
    max,
    compare,
  };
}

function priceHTML(p) {
  const pct = p.compare ? Math.round(((p.compare - p.min) / p.compare) * 100) : 0;
  return `<div class="price"><b>${p.max > p.min ? 'From ' : ''}${money(p.min)}</b>${p.compare ? `<s>${money(p.compare)}</s>` : ''}${pct > 0 ? `<span class="off">${pct}% off</span>` : ''}</div>`;
}

// same markup as snippets/product-card.liquid
function cardHTML(p) {
  const soldOut = !p.available;
  const isNew = p.created && (Date.now() - new Date(p.created)) / 864e5 <= 30;
  const label = p.variants.length > 1 ? 'Choose size' : 'Add to bag';
  return `<div class="card">
    <div class="card__media"><a class="card__link" href="${esc(p.url)}" tabindex="-1" aria-hidden="true">${p.images[0] ? `<img src="${sized(p.images[0], 600)}" alt="${esc(p.title)}" loading="lazy" decoding="async">` : ''}${p.images[1] ? `<img class="alt" data-src="${sized(p.images[1], 600)}" alt="" decoding="async">` : ''}</a>
      ${soldOut ? '<span class="badge badge--soldout">Sold out</span>' : isNew ? '<span class="badge badge--new">New</span>' : ''}
      <button class="wish" data-wish="${esc(p.handle)}" aria-pressed="${wish.has(p.handle)}" aria-label="Save ${esc(p.title)} to wishlist">${ICON.heart}</button>
      <div class="card__actions"><button class="qv-btn" data-qv="${esc(p.handle)}" aria-label="Quick view ${esc(p.title)}">Quick view</button></div></div>
    <a class="card__title" href="${esc(p.url)}">${esc(p.title)}</a>${/anklet/i.test(p.title) ? '<span class="card__piece">Sold as 1 piece</span>' : ''}
    ${priceHTML(p)}
    ${soldOut ? '<button class="card__buy" disabled>Sold out</button>' : `<button class="card__buy" data-quick="${esc(p.handle)}" data-label="${label}" aria-label="${label}: ${esc(p.title)}">${label}</button>`}
  </div>`;
}

/* ---------- toast & overlays ---------- */
function toast(msg, html = false) {
  const t = $('#toast');
  if (!t) return;
  if (html) t.innerHTML = msg; else t.textContent = msg;
  t.classList.toggle('toast--action', html);
  t.classList.add('is-open');
  clearTimeout(toast.timer);
  toast.timer = setTimeout(() => t.classList.remove('is-open'), html ? 3500 : 2600);
}
// After adding, the bag stays closed (client: shoppers open it when they're done); a note offers a shortcut.
function addedToast() {
  if (openPanel === $('#cart')) return;
  toast('<span>Added to your bag</span><button data-open="cart">View bag</button>', true);
}

let openPanel = null;
function open(panel) {
  if (!panel) return;
  close();
  openPanel = panel;
  panel.classList.add('is-open');
  $('#scrim')?.classList.add('is-open');
  document.body.classList.add('locked');
  if (panel.id === 'cart') cart.refresh();
  if (panel.id === 'search') setTimeout(() => $('#search-input')?.focus(), 60);
}
function close() {
  if (!openPanel) return;
  openPanel.classList.remove('is-open');
  $('#scrim')?.classList.remove('is-open');
  document.body.classList.remove('locked');
  openPanel = null;
}

/* ---------- wishlist (saved on this device) ---------- */
const wish = {
  items: store.get('wa-wish', []),
  has(h) { return this.items.includes(h); },
  toggle(h) {
    this.items = this.has(h) ? this.items.filter((x) => x !== h) : [...this.items, h];
    store.set('wa-wish', this.items);
    this.paint();
    return this.has(h);
  },
  paint() {
    $$('[data-wish-count]').forEach((b) => { b.textContent = this.items.length; b.hidden = this.items.length === 0; });
    $$('[data-wish]').forEach((b) => b.setAttribute('aria-pressed', this.has(b.dataset.wish)));
  },
};

const recent = {
  get() { return store.get('wa-recent', []); },
  push(h) { store.set('wa-recent', [h, ...this.get().filter((x) => x !== h)].slice(0, 12)); },
};

function flyToCart(fromEl) {
  const target = $$('[data-open="cart"]').find((b) => b.offsetParent !== null && !b.closest('#toast'));
  const img = fromEl && (fromEl.tagName === 'IMG' ? fromEl : fromEl.closest('.card, .qv, .pdp, .stud, .stackb, .tulip, .upsell__item')?.querySelector('img'));
  if (!target || !img || matchMedia('(prefers-reduced-motion: reduce)').matches) return Promise.resolve();
  const a = img.getBoundingClientRect();
  const b = target.getBoundingClientRect();
  const ghost = img.cloneNode();
  ghost.removeAttribute('srcset');
  ghost.removeAttribute('sizes');
  ghost.className = 'fly-ghost';
  Object.assign(ghost.style, { left: a.left + 'px', top: a.top + 'px', width: a.width + 'px', height: a.height + 'px' });
  document.body.appendChild(ghost);
  requestAnimationFrame(() => {
    Object.assign(ghost.style, { left: b.left + b.width / 2 - 18 + 'px', top: b.top + b.height / 2 - 18 + 'px', width: '36px', height: '36px', opacity: '0.4' });
  });
  return new Promise((done) => setTimeout(() => {
    ghost.remove();
    target.classList.add('bump');
    setTimeout(() => target.classList.remove('bump'), 450);
    done();
  }, 620));
}

/* ---------- small product lists for the bag (cached for the visit) ---------- */
// /collections/<handle>?view=data returns light JSON (templates/collection.data.liquid)
function listData(path, key) {
  const cached = (() => { try { return JSON.parse(sessionStorage.getItem(key)); } catch { return null; } })();
  if (cached && Date.now() - cached.at < 15 * 60 * 1000) return Promise.resolve(cached.items);
  return fetch(path).then((r) => (r.ok ? r.json() : [])).then((items) => {
    try { sessionStorage.setItem(key, JSON.stringify({ at: Date.now(), items })); } catch { /* ignore */ }
    return items;
  }).catch(() => []);
}
const fillersData = () => listData(`${R.collections}/all?view=data&sort_by=best-selling`, 'wa-fillers');
const studData = () => listData(`${R.collections}/${WA.studCollection}?view=data`, 'wa-studs');

/* ---------- bag (Shopify cart) ---------- */
const cart = {
  data: null,
  busy: false,
  async refresh() {
    try {
      const r = await fetch(`${R.cart}.js`, { headers: { Accept: 'application/json' } });
      this.data = await r.json();
    } catch { /* keep the last known bag */ }
    renderCart();
    return this.data;
  },
  async add(items) {
    const r = await fetch(`${R.cartAdd}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items }),
    });
    const res = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(res.description || res.message || 'Could not add to bag');
    await this.refresh();
    return res;
  },
  async change(key, quantity) {
    const r = await fetch(`${R.cartChange}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity }),
    });
    const res = await r.json().catch(() => ({}));
    if (!r.ok) toast(res.description || res.message || 'Could not update the bag');
    await this.refresh();
  },
  saveNote: debounce((note) => {
    fetch(`${R.cartUpdate}.js`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ note }) }).catch(() => {});
  }, 500),
  count() { return this.data ? this.data.item_count : 0; },
  total() { return this.data ? this.data.total_price : 0; },
  // bag value before discounts: what the free-shipping and offer limits are measured on
  value() { return this.data ? this.data.items.reduce((s, i) => s + i.original_line_price, 0) : 0; },
};

// Amounts still to add, rounded up to the next rupee.
const moneyUp = (paise) => money(Math.ceil(paise / 100) * 100);

function shipProgressHTML(total, goal = WA.freeShip || 49900, label = 'free shipping') {
  const pct = Math.min(100, (total / goal) * 100);
  const msg = total < goal
    ? `You're <b>${moneyUp(goal - total)}</b> away from <b>${label}</b>`
    : `<b>You've unlocked ${label}</b>`;
  return `<div class="ship-progress">${msg}<div class="mile-track"><div class="ship-bar"><span style="width:${pct}%"></span></div><div class="mile${total >= goal ? ' is-done' : ''}" style="left:100%"><i></i><span>Free shipping<br>${moneyUp(goal)}</span></div></div></div>`;
}

// Free shipping is a Shopify shipping rate checked on the order total after discounts, set to the free-shipping
// amount less the usual % offer (₹999 less 5% = ₹949.05). Outside a sale the bag measures its value before discounts
// against ₹999, which comes to the same. In a sale the free pieces replace the % offer, so the bag measures its
// total after discounts against this amount instead.
function shipAfterDiscounts() {
  const goal = WA.freeShip || 49900;
  return WA.offerPct > 0 && WA.offerMin <= goal ? Math.round((goal * (100 - WA.offerPct)) / 100) : goal;
}
const saleShipGoal = () => (SALE.shipMin > 0 ? SALE.shipMin : shipAfterDiscounts());
const saleGiftName = () => `<b>free ${esc(SALE.gift)}</b>${SALE.giftNote ? ` (${esc(SALE.giftNote)})` : ''}`;

// The sale's Buy X Get Y counts every piece above ₹50 (gift wrap, gift box and the ₹50 keychain are left out),
// and the free gift and free shipping are checked on the total after the free pieces come off, as Shopify does.
function saleBoxHTML(d) {
  const size = SALE.buy + SALE.get;
  const n = d.items.filter((i) => i.original_price > 5000).reduce((s, i) => s + i.quantity, 0);
  const free = Math.floor(n / size) * SALE.get;
  const rest = n % size;
  const need = rest ? size - rest : 0;
  const pieces = (k) => `${k} piece${k > 1 ? 's' : ''}`;
  let offer;
  if (!n) offer = `Add any ${size} pieces and the lowest-priced one is <b>FREE</b>`;
  else if (!need) offer = `<b>${pieces(free)} FREE</b> in your bag. Add ${size} more for another free piece`;
  else if (free) offer = `<b>${pieces(free)} FREE</b> so far. Add <b>${need} more</b> to get another one free`;
  else offer = `Add <b>${need} more piece${need > 1 ? 's' : ''}</b> and the lowest-priced one is <b>FREE</b>`;
  const gotGift = d.total_price >= SALE.giftMin;
  const gift = gotGift
    ? `${ICON.check}<span>A ${saleGiftName()} comes with this order</span>`
    : `<span>Add <b>${moneyUp(SALE.giftMin - d.total_price)}</b> more for a ${saleGiftName()}</span>`;
  const giftPic = SALE.giftImg ? `<img class="sale-gift" src="${abs(SALE.giftImg)}" alt="" width="60" height="60">` : (gotGift ? '' : ICON.diya);
  return `<div class="sale-box"><span class="sale-box__tag">${ICON.diya}${esc(SALE.name)}</span>
    <p class="${!need && n ? 'is-done' : ''}">${!need && n ? ICON.check : ICON.gift}<span>${offer}</span></p>
    <p class="sale-box__gift${gotGift ? ' is-done' : ''}">${giftPic}${gift}</p></div>`;
}

// What the bag would cost after discounts if `qty` of a piece at `price` were added (the product page uses this
// before Shopify has worked the discount out). Shopify gives the bigger of the free pieces and the usual % offer.
function saleTotalWith(price = 0, qty = 0) {
  const size = SALE.buy + SALE.get;
  const units = [];
  let other = 0;
  (cart.data ? cart.data.items : []).forEach((i) => {
    if (i.original_price > 5000) for (let k = 0; k < i.quantity; k += 1) units.push(i.original_price);
    else other += i.original_line_price;
  });
  for (let k = 0; k < qty; k += 1) { if (price > 5000) units.push(price); else other += price; }
  units.sort((a, b) => a - b);
  const free = Math.floor(units.length / size) * SALE.get;
  const sub = units.reduce((t, p) => t + p, 0) + other;
  const freeOff = units.slice(0, free).reduce((t, p) => t + p, 0);
  const pctOff = WA.offerPct > 0 && sub >= WA.offerMin ? Math.round((sub * WA.offerPct) / 100) : 0;
  return sub - Math.max(freeOff, pctOff);
}

// The free gift goes in the bag as its own ₹0 line (a product set to Unlisted, picked in Theme settings › Festive
// sale), so the shopper sees it at checkout and it shows on the order for packing. The bag adds it once the total
// after discounts reaches the gift amount, takes it out below that or after the sale, and keeps it at 1.
// The "Free Diwali lamp: Yes" note on the order (Additional details in the admin) stays as well.
const isGift = (l) => !!(SALE && SALE.giftVariant && l.variant_id === SALE.giftVariant);
const giftQty = (d) => d.items.filter(isGift).reduce((s, l) => s + l.quantity, 0);
let giftFailed = false;
async function syncSaleGift(d) {
  if (!SALE || !d || !d.items) return false;
  const on = saleOn() && SALE.giftMin > 0;
  const line = d.items.find(isGift);
  const want = on && d.items.some((l) => !isGift(l)) && d.total_price >= SALE.giftMin;
  const post = async (url, body) => {
    const r = await fetch(`${url}.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) });
    return r.ok;
  };
  let changed = false;
  try {
    if (SALE.giftVariant && !giftFailed) {
      let ok = null;
      if (want && !line) ok = await post(R.cartAdd, { items: [{ id: SALE.giftVariant, quantity: 1 }] });
      else if (line && (!want || line.quantity !== 1)) ok = await post(R.cartChange, { id: line.key, quantity: want ? 1 : 0 });
      if (ok) changed = 'line';
      else if (ok === false) giftFailed = true; // e.g. the gift product is missing: don't keep trying on this page
    }
    if (on) {
      const key = `Free ${SALE.gift}`;
      const note = want ? 'Yes' : '';
      if (((d.attributes || {})[key] || '') !== note && await post(R.cartUpdate, { attributes: { [key]: note } })) changed = changed || 'note';
    }
  } catch { /* tried again on the next bag update */ }
  return changed;
}

// One sync at a time. When it changed the bag, or the bag changed meanwhile, the bag reloads and checks again.
let giftBusy = null;
let giftAgain = false;
let giftRounds = 0;
function queueGiftSync(d) {
  if (giftBusy) { giftAgain = true; return; }
  giftBusy = syncSaleGift(d).then((changed) => {
    giftBusy = null;
    const again = giftAgain;
    giftAgain = false;
    if (changed) giftRounds += 1;
    if (giftRounds > 6) return;
    if (changed === 'line' && document.body.dataset.template === 'cart') { location.reload(); return; }
    if (changed || again) cart.refresh();
  });
}

// Checkout first makes sure the gift line is right (the bag may have changed a moment ago).
async function giftReadyForCheckout() {
  if (!SALE || !SALE.giftVariant) return;
  try {
    if (giftBusy) await giftBusy;
    const r = await fetch(`${R.cart}.js`, { headers: { Accept: 'application/json' } });
    await syncSaleGift(await r.json());
  } catch { /* go to checkout anyway */ }
}

const giftLineHTML = (l) => `
    <div class="line line--gift">
      <img src="${l.image ? sized(l.image, 200) : abs(SALE.giftImg || '')}" alt="">
      <div>
        <span class="line__title">${esc(l.product_title)}</span>
        <div class="line__variant">${SALE.giftNote ? `${esc(SALE.giftNote)} · ` : ''}free with your order</div>
      </div>
      <div class="line__right"><b class="line__free">FREE</b></div>
    </div>`;

let cartRenderSeq = 0;
async function renderCart() {
  const seq = ++cartRenderSeq;
  const d = cart.data;
  if (d && SALE) queueGiftSync(d);
  const count = d ? d.item_count - giftQty(d) : 0;
  $$('[data-cart-count]').forEach((b) => { b.textContent = count; b.hidden = count === 0; });
  const sale = saleOn();
  const goal = sale ? saleShipGoal() : (WA.freeShip || 49900);
  const total = d ? d.total_price : 0;
  // normally the bag value before discounts; in the sale, the total after discounts (what Shopify checks)
  const bag = sale ? total : cart.value();
  const freeLabel = sale && SALE.shipMin > 0 && SALE.shipMin === SALE.giftMin ? `free shipping + a free ${esc(SALE.gift)}` : 'free shipping';
  const pill = $('#ship-pill');
  if (pill) {
    pill.hidden = count === 0 || bag >= goal;
    pill.innerHTML = `${ICON.truck}<span>${moneyUp(Math.max(0, goal - bag))} away from ${freeLabel}</span>`;
  }
  const body = $('#cart-body');
  const foot = $('#cart-foot');
  if (!body || !d) return;
  if (!d.items.length) {
    body.innerHTML = `<div class="cart-empty">${ICON.bag}<h3>Your bag is empty</h3><p class="muted">Start with our bestsellers.</p>
      <a class="btn btn-primary" href="${R.collections}/all?sort_by=best-selling">Shop bestsellers</a></div>`;
    foot.hidden = true;
    return;
  }
  const inBag = new Set(d.items.map((i) => i.handle));
  const [fill, studs] = await Promise.all([fillersData(), studData()]);
  if (seq !== cartRenderSeq) return; // a newer render is on its way
  const left = goal - bag;
  const fillers = left > 0 ? fill.filter((p) => p.a && !p.m && !p.x && p.p >= left && p.p <= Math.max(left + 25000, 39900) && !inBag.has(p.h)).slice(0, 3) : [];
  const upsells = [WA.giftBox, WA.giftWrap].filter((g) => g && !inBag.has(g.handle));
  const studSet = new Set(studs.map((s) => s.h));
  const studCount = d.items.filter((i) => studSet.has(i.handle)).reduce((s, i) => s + i.quantity, 0);

  body.innerHTML = (sale ? saleBoxHTML(d) : '') + shipProgressHTML(bag, goal, freeLabel) + d.items.map((l) => (isGift(l) ? giftLineHTML(l) : `
    <div class="line">
      <img src="${sized(l.image, 200)}" alt="">
      <div>
        <a class="line__title" href="${esc(l.url)}">${esc(l.product_title)}</a>
        ${l.variant_title && !/default title/i.test(l.variant_title) ? `<div class="line__variant">${esc(l.variant_title)}</div>` : ''}
        <div class="qty"><button data-qty="${esc(l.key)}" data-to="${l.quantity - 1}" aria-label="Decrease">${ICON.minus}</button><span>${l.quantity}</span><button data-qty="${esc(l.key)}" data-to="${l.quantity + 1}" aria-label="Increase">${ICON.plus}</button></div>
      </div>
      <div class="line__right"><b>${money(l.original_line_price)}</b><button class="line__remove" data-qty="${esc(l.key)}" data-to="0">Remove</button></div>
    </div>`)).join('') +
    (fillers.length ? `<div class="upsell"><h4>Add one to unlock ${freeLabel}</h4><div class="filler-row">${fillers.map((p) => `
      <div class="filler"><img src="${sized(p.i, 200)}" alt=""><span>${esc(p.t)}</span><b>${money(p.p)}</b>
      <button class="btn btn-outline btn-sm" data-quick="${esc(p.h)}">Add</button></div>`).join('')}</div></div>` : '') +
    (upsells.length ? `<div class="upsell"><h4>Make it a gift</h4>${upsells.map((g) => `
      <div class="upsell__item"><img src="${sized(g.image, 120)}" alt=""><div>${esc(g.title)}<br><span class="muted">${money(g.price)}</span></div>
      <button class="btn btn-outline btn-sm" data-quick="${esc(g.handle)}">Add</button></div>`).join('')}</div>` : '') +
    `<label class="gift-note"><span>Add a gift message <em>(free)</em></span><textarea id="gift-note" rows="2" maxlength="200" placeholder="Happy birthday! With love…">${esc(d.note || '')}</textarea></label>`;

  // discounts Shopify applied (e.g. the stack offer), grouped by name
  const discounts = {};
  d.items.forEach((i) => (i.line_level_discount_allocations || []).forEach((a) => {
    const t = a.discount_application?.title || 'Discount';
    discounts[t] = (discounts[t] || 0) + a.amount;
  }));
  (d.cart_level_discount_applications || []).forEach((a) => { discounts[a.title || 'Discount'] = (discounts[a.title || 'Discount'] || 0) + a.total_allocated_amount; });
  const original = d.items.reduce((s, i) => s + i.original_line_price, 0);
  const more = 3 - studCount;
  const offerLeft = WA.offerPct > 0 ? WA.offerMin - bag : 0;
  foot.hidden = false;
  foot.innerHTML = `<div class="cart-total"><span>Subtotal</span><b>${money(original)}</b></div>
    ${Object.entries(discounts).map(([t, amt]) => `<div class="cart-offer"><span>${esc(t)}</span><b>−${money(amt)}</b></div>`).join('')}
    ${!sale && studCount > 0 && more > 0 ? `<a class="cart-offer-hint" href="${R.collections}/${esc(WA.studCollection)}">Add ${more} more ball back stud${more > 1 ? 's' : ''} and get ${WA.stackPercent}% off ${ICON.arrow}</a>` : ''}
    ${!sale && offerLeft > 0 ? `<div class="cart-offer-hint">Add ${money(offerLeft)} more and get ${WA.offerPct}% off your order</div>` : ''}
    <p class="cart-note">Prepaid orders only. Shipping calculated at checkout.</p>
    <a class="btn btn-primary btn-block" href="${ROOT}/checkout" data-checkout>Checkout · ${money(total)}</a>`;
}

async function quickAdd(handle, fromEl) {
  let p;
  try { p = await getProduct(handle); } catch { toast('Sorry, this piece could not be loaded'); return; }
  const v = p.variants.find((x) => x.available);
  if (p.variants.length > 1 || !v) { openQuickView(handle); return; }
  try {
    await cart.add([{ id: v.id, quantity: 1 }]);
  } catch (err) { toast(err.message); return; }
  if (fromEl && fromEl.classList.contains('card__buy')) {
    fromEl.textContent = 'Added ✓';
    fromEl.classList.add('is-added');
    setTimeout(() => { fromEl.textContent = fromEl.dataset.label || 'Add to bag'; fromEl.classList.remove('is-added'); }, 1600);
  }
  flyToCart(fromEl);
  addedToast();
}

/* ---------- quick view ---------- */
async function openQuickView(handle) {
  let p;
  try { p = await getProduct(handle); } catch { toast('Sorry, this piece could not be loaded'); return; }
  const first = p.variants.find((v) => v.available) || p.variants[0];
  const selected = p.options.map((o, i) => first.opts[i]);
  const current = () => p.variants.find((v) => p.options.every((o, i) => v.opts[i] === selected[i])) || p.variants[0];
  const box = $('#qv-body');
  const draw = () => {
    const v = current();
    const off = v.compare > v.price ? Math.round((1 - v.price / v.compare) * 100) : 0;
    box.innerHTML = `
      <div class="qv__gallery">${p.images.map((im, i) => `<img src="${sized(im, 900)}" alt="${i ? '' : esc(p.title)}">`).join('')}</div>
      <div class="qv__info">
        ${off ? `<span class="pdp-tag">-${off}%</span>` : ''}
        <h3>${esc(p.title)}</h3>
        <div class="pdp-price"><b>${money(v.price)}</b>${off ? `<s>${money(v.compare)}</s>` : ''}</div>
        ${/anklet/i.test(p.title) ? '<p class="pdp-piece">Sold as 1 piece (a single anklet), not a pair</p>' : ''}
        <p class="muted" style="margin:8px 0 0">${v.available ? 'In stock, ready to ship' : 'Sold out'}</p>
        ${p.options.map((o, i) => `<div class="opt-label">${esc(o.name)}: ${esc(selected[i])}</div><div class="pills">${o.values.map((val) => {
          const ok = p.variants.some((x) => x.opts[i] === val && x.available);
          return `<button data-qvopt="${i}" data-val="${esc(val)}" aria-pressed="${selected[i] === val}" class="${ok ? '' : 'na'}">${esc(val)}</button>`;
        }).join('')}</div>`).join('')}
        <button class="btn btn-primary btn-block" id="qv-add" style="margin-top:20px" ${v.available ? '' : 'disabled'}>${v.available ? 'Add to bag' : 'Sold out'}</button>
        <a class="link-arrow" href="${esc(p.url)}" style="margin-top:16px">View full details ${ICON.arrow}</a>
      </div>`;
    $$('[data-qvopt]', box).forEach((b) => b.addEventListener('click', () => { selected[Number(b.dataset.qvopt)] = b.dataset.val; draw(); }));
    $('#qv-add', box).addEventListener('click', async (e) => {
      const btn = e.currentTarget;
      const cv = current();
      if (!cv.available) return;
      btn.disabled = true;
      try { await cart.add([{ id: cv.id, quantity: 1 }]); } catch (err) { toast(err.message); btn.disabled = false; return; }
      btn.textContent = 'Added ✓';
      flyToCart(btn).then(() => close());
      addedToast();
    });
  };
  draw();
  open($('#qv'));
}

/* ---------- search (Shopify predictive search) ---------- */
let searchSeq = 0;
async function renderSearch(q) {
  const out = $('#search-out');
  if (!out) return;
  const query = q.trim();
  if (!query) {
    const popular = (out.dataset.popular || '').split(',').map((t) => t.trim()).filter(Boolean);
    out.innerHTML = popular.length ? `<div class="search-hint"><p class="muted" style="margin-bottom:12px">Popular searches</p><div class="chip-row">
      ${popular.map((t) => `<button class="chip" data-search="${esc(t)}">${esc(t)}</button>`).join('')}</div></div>` : '';
    return;
  }
  const seq = ++searchSeq;
  let products = [];
  try {
    const url = `${R.suggest}.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=8&resources[options][unavailable_products]=last`;
    const res = await (await fetch(url)).json();
    products = (res.resources?.results?.products || []).map(normalize);
  } catch { /* show no results */ }
  if (seq !== searchSeq) return;
  out.innerHTML = products.length
    ? `<div class="search-results">${products.map(cardHTML).join('')}</div>
       <p style="text-align:center;margin:22px 0 0"><a class="btn btn-outline" href="${R.search}?q=${encodeURIComponent(query)}&type=product&options%5Bunavailable_products%5D=last">See all results ${ICON.arrow}</a></p>`
    : `<div class="search-hint"><p>No results for “${esc(query)}”.</p></div>`;
}

/* ---------- header, drawers and page chrome ---------- */
function initChrome() {
  const hintEl = $('#search-hint');
  const hints = (hintEl?.dataset.hints || '').split(',').map((t) => t.trim()).filter(Boolean);
  let hintAt = 0;
  if (hintEl && hints.length > 1) setInterval(() => {
    if (document.hidden) return;
    hintEl.classList.add('is-out');
    setTimeout(() => { hintAt = (hintAt + 1) % hints.length; hintEl.textContent = hints[hintAt]; hintEl.classList.remove('is-out'); }, 300);
  }, 2800);
  const sale = saleOn();
  const msgs = $$('.announce__msg').filter((m) => (sale ? !m.classList.contains('no-sale') : !m.classList.contains('sale-only')));
  $$('.announce__msg').forEach((m) => m.classList.remove('is-active', 'is-first-sale'));
  msgs[0]?.classList.add('is-active');
  let mi = 0;
  if (msgs.length > 1) setInterval(() => { msgs[mi].classList.remove('is-active'); mi = (mi + 1) % msgs.length; msgs[mi].classList.add('is-active'); }, 4000);

  const mega = $('#mega');
  const toggle = $('[data-mega-toggle]');
  const setMega = (on) => { if (!mega || !toggle) return; mega.classList.toggle('is-open', on); toggle.setAttribute('aria-expanded', on); };
  if (toggle) {
    toggle.addEventListener('mouseenter', () => setMega(true));
    toggle.addEventListener('click', () => setMega(matchMedia('(hover: hover)').matches || !mega.classList.contains('is-open')));
    $('#site-header')?.addEventListener('mouseleave', () => setMega(false));
    $$('.hd-nav a').forEach((a) => a.addEventListener('mouseenter', () => setMega(false)));
  }

  // account menu under the person icon (laptop)
  const acctBtn = $('[data-acct]');
  const acctPop = $('#acct-pop');
  const placeAcct = () => {
    const r = acctBtn.getBoundingClientRect();
    const right = Math.max(12, innerWidth - r.right - 4);
    acctPop.style.top = `${r.bottom + 8}px`;
    acctPop.style.right = `${right}px`;
    acctPop.style.setProperty('--arrow', `${innerWidth - right - r.left - r.width / 2 - 5}px`);
  };
  const setAcct = (on) => {
    if (!acctBtn || !acctPop) return;
    if (on) placeAcct();
    acctPop.classList.toggle('is-open', on);
    acctBtn.setAttribute('aria-expanded', on);
  };
  if (acctBtn && acctPop) {
    acctBtn.addEventListener('click', (e) => { e.preventDefault(); setMega(false); setAcct(!acctPop.classList.contains('is-open')); });
    document.addEventListener('click', (e) => { if (!e.target.closest('#acct-pop, [data-acct]')) setAcct(false); });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') setAcct(false); });
    addEventListener('scroll', () => { if (acctPop.classList.contains('is-open')) placeAcct(); }, { passive: true });
    addEventListener('resize', () => setAcct(false));
  }

  // track page: copy the Speed Post number, then open India Post
  const trackForm = $('#track-form');
  if (trackForm) trackForm.addEventListener('submit', (e) => {
    e.preventDefault();
    const no = trackForm.n.value.replace(/\s+/g, '').toUpperCase();
    if (!no) return;
    trackForm.n.value = no;
    navigator.clipboard?.writeText(no).catch(() => {});
    window.open(trackForm.action, '_blank', 'noopener');
    const hint = $('#track-hint'); if (hint) hint.hidden = false;
  });

  const input = $('#search-input');
  if (input) input.addEventListener('input', debounce((e) => renderSearch(e.target.value), 220));
  renderSearch('');

  document.addEventListener('input', (e) => { if (e.target.id === 'gift-note') cart.saveNote(e.target.value); });

  document.addEventListener('click', (e) => {
    const co = e.target.closest('a[data-checkout]');
    if (co && SALE && SALE.giftVariant) { e.preventDefault(); co.classList.add('is-busy'); giftReadyForCheckout().then(() => { location.href = co.href; }); return; }
    const qv = e.target.closest('[data-qv]');
    if (qv) { e.preventDefault(); openQuickView(qv.dataset.qv); return; }
    const cp = e.target.closest('[data-copy]');
    if (cp) {
      navigator.clipboard?.writeText(cp.dataset.copy).catch(() => {});
      const s = cp.querySelector('span'); if (s) s.textContent = 'Copied!';
      toast(`Code ${cp.dataset.copy} copied`);
      if (cp.closest('#welcome')) setTimeout(() => { if (openPanel?.id === 'welcome') close(); }, 1100);
      return;
    }
    if (e.target.closest('#to-top')) { window.scrollTo({ top: 0, behavior: 'smooth' }); return; }
    const w = e.target.closest('[data-wish]');
    if (w) {
      e.preventDefault();
      toast(wish.toggle(w.dataset.wish) ? 'Saved to your wishlist' : 'Removed from your wishlist');
      if (page === 'wishlist') renderWishlist();
      return;
    }
    const t = e.target.closest('[data-open],[data-close],[data-quick],[data-qty],[data-search],#scrim');
    if (!t) return;
    if (t.id === 'scrim' || t.hasAttribute('data-close')) { e.preventDefault(); close(); return; }
    if (t.dataset.open) {
      e.preventDefault();
      t.closest('#toast')?.classList.remove('is-open');
      open($('#' + t.dataset.open));
      return;
    }
    if (t.dataset.quick) { e.preventDefault(); quickAdd(t.dataset.quick, t); return; }
    if (t.dataset.qty) { e.preventDefault(); cart.change(t.dataset.qty, Math.max(0, Number(t.dataset.to))); return; }
    if (t.dataset.search) { const i = $('#search-input'); if (i) i.value = t.dataset.search; renderSearch(t.dataset.search); }
  });
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') { close(); setMega(false); } });

  // header shrinks on scroll, a thin reading-progress line, back-to-top button
  const prog = $('#progress-top');
  const toTop = $('#to-top');
  const siteHeader = $('#site-header');
  let maxScroll = 1;
  const measure = () => { maxScroll = Math.max(1, document.documentElement.scrollHeight - innerHeight); };
  if ('ResizeObserver' in window) new ResizeObserver(measure).observe(document.body);
  window.addEventListener('resize', measure, { passive: true });
  measure();
  let ticking = false; let topShown = false; let compactNow = false;
  const onScroll = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      const y = window.scrollY;
      if (prog) prog.style.transform = `scaleX(${Math.min(1, y / maxScroll)})`;
      const compact = y > 60;
      if (compact !== compactNow) { compactNow = compact; siteHeader?.classList.toggle('is-scrolled', compact); }
      const show = y > 1200;
      if (show !== topShown) { topShown = show; toTop?.classList.toggle('is-visible', show); }
      window.dispatchEvent(new CustomEvent('wa-scroll', { detail: y / maxScroll }));
      ticking = false;
    });
  };
  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();

  // load a card's second photo only when someone actually hovers it
  document.addEventListener('pointerover', (e) => {
    const card = e.target.closest && e.target.closest('.card');
    const alt = card && card.querySelector('img.alt[data-src]');
    if (alt) { alt.src = alt.dataset.src; alt.removeAttribute('data-src'); }
  }, { passive: true });

  // a form posted to Shopify (newsletter, contact, notify) scrolls back to its message
  if (location.search.includes('customer_posted=true') || location.search.includes('contact_posted=true') || location.hash.includes('newsletter-form')) {
    $('#newsletter-form')?.scrollIntoView({ block: 'center' });
  }
}

/* ---------- welcome offer popup ---------- */
function initWelcome() {
  const box = $('#welcome');
  const seenKey = saleOn() ? `wa-welcome-sale-${SALE.start}` : 'wa-welcome-seen';
  if (!box || store.get(seenKey, false) || page === 'product' || window.Shopify?.designMode) return;
  let shown = false;
  const show = () => {
    if (shown || openPanel) return;
    shown = true;
    store.set(seenKey, true);
    open(box);
  };
  setTimeout(show, saleOn() ? 8000 : 15000);
  const onProgress = (e) => { if (e.detail > 0.45) { window.removeEventListener('wa-scroll', onProgress); show(); } };
  window.addEventListener('wa-scroll', onProgress);
  document.addEventListener('mouseout', (e) => { if (!e.relatedTarget && e.clientY < 8) show(); });
}

function initMotion() {
  const items = $$('.reveal');
  if (!('IntersectionObserver' in window) || matchMedia('(prefers-reduced-motion: reduce)').matches) { items.forEach((el) => el.classList.add('is-in')); return; }
  const io = new IntersectionObserver((entries) => entries.forEach((en) => {
    if (en.isIntersecting) { en.target.classList.add('is-in'); io.unobserve(en.target); }
  }), { rootMargin: '0px 0px -8% 0px' });
  items.forEach((el) => io.observe(el));
}

function wireCarousels(root = document) {
  $$('[data-carousel]', root).forEach((wrap) => {
    const track = $('.carousel__track', wrap);
    $$('[data-dir]', wrap).forEach((b) => b.addEventListener('click', () => track.scrollBy({ left: Number(b.dataset.dir) * track.clientWidth * 0.9, behavior: 'smooth' })));
  });
}

/* ---------- homepage ---------- */
/* Film strip: drifts on its own; on phones it can be swiped, and it drifts again shortly after you let go. */
function runStrip(strip) {
  const track = $('.strip__track', strip);
  if (!track || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  strip.classList.add('strip--js');
  let onScreen = true;
  new IntersectionObserver(([en]) => { onScreen = en.isIntersecting; }).observe(strip);
  let loop = track.offsetWidth / 2; // the cards are listed twice, so one loop is half the track
  let x = 0, v = 0, speed = 0, last = 0, resumeAt = 0, moved = 0, hover = false, drag = null;
  const wrap = (n) => (loop ? ((n % loop) + loop) % loop : 0);
  const paint = () => { track.style.transform = `translate3d(${-x}px,0,0)`; };
  const frame = (t) => {
    const dt = last ? Math.min(t - last, 50) : 16;
    last = t;
    if (!drag && onScreen && !document.hidden) {
      const cruise = hover || t < resumeAt ? 0 : loop / 60000; // one full loop a minute
      speed += (cruise - speed) * Math.min(1, dt / 500);
      v = Math.abs(v) < 0.005 ? 0 : v * Math.pow(0.95, dt / 16);
      x = wrap(x + (speed + v) * dt);
      paint();
    }
    requestAnimationFrame(frame);
  };
  requestAnimationFrame(frame);
  const release = (e) => {
    if (!drag) return;
    if (e.type === 'pointercancel' || e.timeStamp - drag.lt > 90) v = 0;
    drag = null;
    resumeAt = performance.now() + 1200;
  };
  strip.addEventListener('pointerdown', (e) => {
    moved = 0;
    if (e.pointerType === 'mouse') return;
    drag = { x: e.clientX, from: x, lx: e.clientX, lt: e.timeStamp };
    v = 0; speed = 0;
  });
  window.addEventListener('pointermove', (e) => {
    if (!drag || e.pointerType === 'mouse') return;
    const dx = e.clientX - drag.x;
    moved = Math.max(moved, Math.abs(dx));
    const dt = e.timeStamp - drag.lt;
    if (dt > 0) v = Math.max(-2.5, Math.min(2.5, 0.7 * (-(e.clientX - drag.lx) / dt) + 0.3 * v));
    drag.lx = e.clientX; drag.lt = e.timeStamp;
    x = wrap(drag.from - dx);
    paint();
  }, { passive: true });
  window.addEventListener('pointerup', release);
  window.addEventListener('pointercancel', release);
  strip.addEventListener('click', (e) => { if (moved > 8) e.preventDefault(); }, true);
  strip.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse') hover = true; });
  strip.addEventListener('pointerleave', (e) => { if (e.pointerType === 'mouse') hover = false; });
  if ('ResizeObserver' in window) new ResizeObserver(() => { loop = track.offsetWidth / 2; x = wrap(x); }).observe(track);
}

function initHome() {
  $$('.strip').forEach(runStrip);

  // Bestsellers / New in
  const track = $('#tab-track');
  if (track) {
    const firstHTML = track.innerHTML;
    let newHTML = null;
    $$('[data-tab]').forEach((b) => b.addEventListener('click', async () => {
      $$('[data-tab]').forEach((x) => x.setAttribute('aria-selected', x === b));
      const link = $('#tab-link');
      if (link && b.dataset.link) link.href = b.dataset.link;
      if (!b.dataset.feed) { track.innerHTML = firstHTML; wish.paint(); return; }
      if (newHTML === null) {
        track.style.opacity = '0.4';
        try {
          const html = await (await fetch(b.dataset.feed)).text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          newHTML = doc.querySelector('.grid')?.innerHTML || '';
        } catch { newHTML = ''; }
        track.style.opacity = '';
      }
      if (b.getAttribute('aria-selected') === 'true') { track.innerHTML = newHTML || firstHTML; wish.paint(); }
    }));
  }

  // Tulip colour picker
  $$('[data-tulip]').forEach((b) => b.addEventListener('click', () => {
    $$('[data-tulip]').forEach((x) => x.setAttribute('aria-checked', x === b));
    const img = $('#tulip-img');
    if (img) {
      img.classList.add('is-swapping');
      setTimeout(() => { img.removeAttribute('srcset'); img.src = b.dataset.img; img.alt = b.dataset.title; img.classList.remove('is-swapping'); }, 180);
    }
    $('#tulip-name').textContent = b.dataset.title;
    $('#tulip-price').textContent = b.dataset.price;
    const add = $('#tulip-add');
    add.dataset.qv = b.dataset.handle;
    add.textContent = b.dataset.btn;
  }));

  // Build your own stack
  const stackb = $('.stackb');
  if (stackb) {
    const percent = Number(stackb.dataset.stackPercent || WA.stackPercent || 0);
    const picked = [];
    const more = $('#stack-more');
    more?.addEventListener('click', () => {
      const all = stackb.classList.toggle('is-all');
      more.setAttribute('aria-expanded', all);
      more.textContent = all ? 'Show fewer' : 'View all';
      if (!all) stackb.scrollIntoView({ block: 'start' });
    });
    const studEl = (h) => $(`[data-stud="${CSS.escape(h)}"]`, stackb);
    const draw = () => {
      $$('[data-stud]', stackb).forEach((b) => b.setAttribute('aria-pressed', picked.includes(b.dataset.stud)));
      $('#stack-slots').innerHTML = [0, 1, 2].map((k) => {
        const el = picked[k] && studEl(picked[k]);
        return el ? `<span class="slot"><img src="${abs(el.dataset.img)}" alt=""></span>` : `<span class="slot slot--empty">${k + 1}</span>`;
      }).join('');
      const total = picked.reduce((s, h) => s + Number(studEl(h)?.dataset.price || 0), 0);
      const off = picked.length >= 3 ? Math.round((total * percent) / 100) : 0;
      $('#stack-total').innerHTML = !picked.length ? `Pick 3 studs and get ${percent}% off`
        : off ? `<s>${money(total)}</s> <b>${money(total - off)}</b> <span class="stackb__save">${percent}% off · you save ${money(off)}</span>`
        : `<b>${money(total)}</b> · pick ${3 - picked.length} more for ${percent}% off`;
      const add = $('#stack-add');
      add.disabled = picked.length === 0;
      add.textContent = picked.length ? `Add ${picked.length === 3 ? 'my stack' : `${picked.length} stud${picked.length > 1 ? 's' : ''}`}` : 'Add my stack';
    };
    $$('[data-stud]', stackb).forEach((b) => b.addEventListener('click', () => {
      const h = b.dataset.stud;
      const at = picked.indexOf(h);
      if (at >= 0) picked.splice(at, 1); else { if (picked.length === 3) picked.shift(); picked.push(h); }
      draw();
    }));
    $('#stack-add')?.addEventListener('click', async (e) => {
      const btn = e.currentTarget;
      const items = picked.map((h) => ({ id: Number(studEl(h).dataset.variant), quantity: 1 }));
      btn.disabled = true;
      try { await cart.add(items); } catch (err) { toast(err.message); btn.disabled = false; return; }
      flyToCart(stackb.querySelector('.stud[aria-pressed="true"]'));
      addedToast();
      btn.disabled = false;
    });
    draw();
  }
}

/* ---------- category pages ---------- */
function initCollection() {
  const sort = $('[data-sort]');
  sort?.addEventListener('change', () => {
    const u = new URL(location.href);
    u.searchParams.set('sort_by', sort.value);
    u.searchParams.delete('page');
    location.href = u.toString();
  });
  // drop empty price boxes so the address stays tidy
  $('#filter-form')?.addEventListener('submit', (e) => {
    $$('input[type="number"]', e.currentTarget).forEach((i) => { if (i.value === '') i.disabled = true; });
  });
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('#load-more');
    if (!btn) return;
    e.preventDefault();
    const later = $$('#grid > .card.is-later');
    if (later.length) {
      const per = +btn.dataset.per || 24;
      later.slice(0, per).forEach((c) => c.classList.remove('is-later'));
      const total = +btn.dataset.total || 0;
      const shownEl = $('#more [data-shown]');
      const shown = (+shownEl?.textContent || 0) + Math.min(per, later.length);
      if (shownEl) shownEl.textContent = shown;
      const bar = $('#more [data-bar]'); if (bar && total) bar.style.width = `${Math.min(100, (shown * 100) / total)}%`;
      if (later.length <= per && btn.getAttribute('href') === '#more') btn.remove();
      return;
    }
    if (btn.getAttribute('href') === '#more') { btn.remove(); return; }
    const section = btn.closest('[data-collection-section]')?.dataset.collectionSection;
    btn.textContent = 'Loading…';
    try {
      const url = new URL(btn.href, location.origin);
      if (section) url.searchParams.set('section_id', section);
      const doc = new DOMParser().parseFromString(await (await fetch(url)).text(), 'text/html');
      const grid = $('#grid');
      const isOut = (c) => !!c.querySelector('.badge--soldout, .card__buy[disabled]');
      $$('#grid > .card', doc).forEach((c) => {
        const firstOut = isOut(c) ? null : $$('#grid > .card').find(isOut);
        if (firstOut) grid.insertBefore(c, firstOut); else grid.appendChild(c);
      });
      const next = $('#more', doc);
      if (next) $('#more').replaceWith(next);
      history.replaceState(null, '', btn.href.replace(/&?section_id=[^&]*/, ''));
      wish.paint();
    } catch { location.href = btn.href; }
  });
}

/* ---------- product page ---------- */
function initProduct() {
  const json = $('#product-json');
  if (!json) return;
  const p = normalize(JSON.parse(json.textContent));
  recent.push(p.handle);
  const urlVariant = Number(new URLSearchParams(location.search).get('variant'));
  const start = p.variants.find((v) => v.id === urlVariant) || p.variants.find((v) => v.available) || p.variants[0];
  const selected = p.options.map((o, i) => start.opts[i]);
  const current = () => p.variants.find((v) => p.options.every((o, i) => v.opts[i] === selected[i])) || p.variants[0];
  let qty = 1;

  function draw() {
    const v = current();
    const off = v.compare > v.price ? Math.round((1 - v.price / v.compare) * 100) : 0;
    const tag = $('#pdp-tag'); tag.hidden = !off; tag.textContent = off ? `-${off}%` : '';
    $('#price').innerHTML = `<div class="pdp-price"><b>${money(v.price)}</b>${off ? `<s>${money(v.compare)}</s><span class="save">Save ${off}%</span>` : ''}</div>`;
    $('#sticky-price').textContent = money(v.price);
    $('#stock').innerHTML = v.available ? '<span class="dot dot--in"></span> In stock, ready to ship' : '<span class="dot dot--out"></span> Sold out';
    $('#options').innerHTML = p.options.map((o, i) => `<div class="opt-label">${esc(o.name)}: ${esc(selected[i])}${/size/i.test(o.name) ? `<a href="${ROOT}/pages/faq#sizing">Size guide</a>` : ''}</div>
      <div class="pills">${o.values.map((val) => {
        const ok = p.variants.some((x) => x.opts[i] === val && x.available);
        return `<button type="button" data-opt="${i}" data-val="${esc(val)}" aria-pressed="${selected[i] === val}" class="${ok ? '' : 'na'}">${esc(val)}</button>`;
      }).join('')}</div>`).join('');
    $('#variant-id').value = v.id;
    const atc = $('#atc');
    atc.disabled = !v.available;
    atc.textContent = v.available ? 'Add to bag' : 'Sold out';
    $('#sticky-atc').disabled = !v.available;
    $('#notify')?.classList.toggle('is-open', !v.available);
    const sale = saleOn();
    const after = sale ? saleTotalWith(v.available ? v.price : 0, v.available ? qty : 0) : cart.value() + (v.available ? v.price * qty : 0);
    const goal = sale ? saleShipGoal() : (WA.freeShip || 49900);
    const tick = ICON.check.replace('<svg', '<svg width="16" height="16" style="vertical-align:-3px;color:#4f8a5b"');
    const bar = (to) => `<div class="ship-bar"><span style="width:${Math.min(100, (after / to) * 100)}%"></span></div>`;
    if (sale && SALE.giftMin) {
      // in the sale this line leads with the free gift, then free shipping
      $('#ship').innerHTML = after < SALE.giftMin
        ? `Add <b>${moneyUp(SALE.giftMin - after)}</b> more to your bag for a ${saleGiftName()}${bar(SALE.giftMin)}`
        : after < goal
          ? `${tick} This order gets a ${saleGiftName()}. Add <b>${moneyUp(goal - after)}</b> more for free shipping`
          : `${tick} This order gets a ${saleGiftName()} and ships free`;
    } else {
      $('#ship').innerHTML = after < goal
        ? `Add <b>${moneyUp(goal - after)}</b> more to your bag for free shipping${bar(goal)}`
        : `${tick} This order ships free`;
    }
    if (p.variants.length > 1) {
      const u = new URL(location.href);
      u.searchParams.set('variant', v.id);
      history.replaceState(null, '', u.toString());
    }
  }

  async function addToBag(e) {
    e.preventDefault();
    const v = current();
    if (!v.available) return;
    const items = [{ id: v.id, quantity: qty }];
    if ($('#gift')?.checked && WA.giftBox) items.push({ id: WA.giftBox.variant, quantity: 1 });
    const btn = $('#atc');
    btn.disabled = true;
    try { await cart.add(items); } catch (err) { toast(err.message); btn.disabled = false; return; }
    btn.disabled = false;
    flyToCart(btn);
    addedToast();
    draw();
  }

  $('#product-form')?.addEventListener('submit', addToBag);
  $('#sticky-atc')?.addEventListener('click', addToBag);
  $('.pdp')?.addEventListener('click', (e) => {
    const opt = e.target.closest('[data-opt]');
    if (opt) { selected[Number(opt.dataset.opt)] = opt.dataset.val; draw(); return; }
    const q = e.target.closest('[data-q]');
    if (q) { qty = Math.max(1, qty + Number(q.dataset.q)); $('#qty').textContent = qty; draw(); return; }
    const th = e.target.closest('[data-thumb]');
    if (th) { const s = $('#stage'); s.scrollTo({ left: s.clientWidth * Number(th.dataset.thumb), behavior: 'smooth' }); }
  });
  const stage = $('#stage');
  stage?.addEventListener('scroll', () => {
    const i = Math.round(stage.scrollLeft / stage.clientWidth);
    $$('[data-thumb]').forEach((b, k) => b.setAttribute('aria-current', k === i));
    $$('.dots span').forEach((d, k) => d.classList.toggle('on', k === i));
  }, { passive: true });
  const buyRow = $('.buy-row');
  if (buyRow) new IntersectionObserver(([en]) => $('#sticky')?.classList.toggle('is-visible', !en.isIntersecting && en.boundingClientRect.top < 0)).observe(buyRow);
  window.addEventListener('wa-cart', draw);
  draw();
}

// "People also bought" from Shopify's product recommendations
async function initRecommendations() {
  const slot = $('[data-recos-url]');
  if (!slot) return;
  try {
    const doc = new DOMParser().parseFromString(await (await fetch(slot.dataset.recosUrl)).text(), 'text/html');
    const filled = $('[data-recos]', doc);
    if (filled && $('.card', filled)) { slot.replaceWith(filled); wish.paint(); wireCarousels(filled); }
  } catch { /* leave it empty */ }
}

async function renderHandles(handles, el) {
  const items = (await Promise.all(handles.map((h) => getProduct(h).catch(() => null)))).filter(Boolean);
  el.innerHTML = items.map(cardHTML).join('');
  wish.paint();
  return items.length;
}

function initRecent() {
  const box = $('[data-recent]');
  if (!box) return;
  const here = box.dataset.current;
  const handles = recent.get().filter((h) => h !== here).slice(0, 8);
  if (!handles.length) return;
  renderHandles(handles, $('.carousel__track', box)).then((n) => { if (n) box.hidden = false; });
}

function renderWishlist() {
  const grid = $('#wishlist-grid');
  if (!grid) return;
  const note = $('#wishlist-note');
  const n = wish.items.length;
  if (note) note.textContent = n ? `${n} saved piece${n > 1 ? 's' : ''}` : 'Tap the heart on any piece to save it here.';
  $('#wishlist-empty').hidden = n > 0;
  if (!n) { grid.innerHTML = ''; return; }
  renderHandles(wish.items, grid);
}

// Bag page: "Checkout" saves the quantities, puts the free gift line right, then opens checkout.
function initCartPage() {
  const form = document.getElementById('cart-page-form');
  if (!form || !SALE || !SALE.giftVariant) return;
  form.addEventListener('submit', async (e) => {
    if (!e.submitter || e.submitter.name !== 'checkout') return;
    e.preventDefault();
    e.submitter.disabled = true;
    try { await fetch(`${R.cartUpdate}.js`, { method: 'POST', body: new FormData(form), headers: { Accept: 'application/json' } }); } catch { /* checkout anyway */ }
    await giftReadyForCheckout();
    location.href = `${ROOT}/checkout`;
  });
}

/* ---------- start ---------- */
function boot() {
  initChrome();
  wish.paint();
  cart.refresh().then(() => window.dispatchEvent(new CustomEvent('wa-cart')));
  if (page === 'home') initHome();
  if (page === 'collection') initCollection();
  if (page === 'product') { initProduct(); initRecommendations(); initRecent(); }
  if (page === 'wishlist') renderWishlist();
  initCartPage();
  wireCarousels();
  initMotion();
  initWelcome();
  // theme editor: re-run section scripts when a section is changed
  document.addEventListener('shopify:section:load', () => { wish.paint(); if (page === 'home') initHome(); });
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
})();
